import React, { useCallback, useEffect, useRef, useState } from 'react';
import showToast from '../../../utils/toast';
import ButtonCancelForm from '../../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../../components/Base/ConfirmAlert';
import usePassesService from '../../../services/usePassesService';
import { Passes } from '../../../models/PassesModel';
import PassesTable from './PassesTable';
import PassesForm from '../../../components/Companies/PasseForm';
import { useFormErrors } from '../../../hooks/useFormErrors';
import TableTextSearch from '../../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../../components/Base/MaintainerPageShell';
import { Plus, Ticket } from 'lucide-react';
import './PassesPage.css';

const PassesPage: React.FC = () => {
  const svc = usePassesService();
  const { list, create, store, edit, update, remove, fetching } = svc;

  const [passes, setPasses] = useState<Passes[]>([]);
  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Passes | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page: 1 };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    list(filters as Record<string, string | number>, {
      onSuccess: (resp) => {
        const data = resp?.data ?? {};
        setPasses(data.passes ?? []);
      },
      onError: (r) => showToast(r?.message ?? 'Error cargando pases', 'error'),
    }, ctrl.signal);
  }, [list]);

  const openCreate = () => {
    create({
      onSuccess: (resp) => {
        const listData = resp?.data ?? [];
        const mapped = listData.map((c: any) => ({ value: String(c.id), label: c.name }));
        setCompanies(mapped);
        setEditing({ name: '', company: '' });
        setIsDetailView(false);
        setShowForm(true);
      },
      onError: () => showToast('Error al cargar formulario', 'error'),
    });
  };

  const openEdit = (p: Passes) => {
    edit(p.id!, {
      onSuccess: (resp) => {
        const data = resp?.data ?? {};
        const item = data.passes ?? {};
        const comps = data.companies ?? [];
        setEditing({ ...item, company: item.company_id ?? '' });
        setCompanies(comps.map((c: any) => ({ value: String(c.id), label: c.name })));
        setIsDetailView(true);
        setShowForm(true);
      },
      onError: () => showToast('Error cargando pase', 'error'),
    });
  };

  const handleEditingChange = (next: Passes) => {
    setEditing(next);
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);

    try {
      if (editing.id) {
        const r = await update(editing.id, { name: editing.name, company: editing.company });
        return r;
      } else {
        const r = await store({ name: editing.name, company: editing.company });
        return r;
      }
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      load(searchTextRef.current.trim() || undefined);
      return;
    }
    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const openDeleteConfirmation = (id: number) => {
    setDeleteId(id);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId === null) return;
    remove(deleteId, {
      onSuccess: (r) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        load(searchTextRef.current.trim() || undefined);
      },
      onError: (r) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });
    setIsAlertOpen(false);
    setDeleteId(null);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
    setIsDetailView(false);
  };

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [load]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(value), 400);
    },
    [load]
  );

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">{passes.length}</span>
        pases
      </span>
    );

  return (
    <MaintainerPageShell
      title="Pases"
      icon={<Ticket size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo pase
        </button>
      }
    >
      <PassesTable
        data={passes}
        loading={Boolean(fetching.list)}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={passes.length}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre de pase o empresa..."
            aria-label="Buscar pases por nombre o empresa"
          />
        }
      />

      {showForm && editing && (
        <div className="authorizers-modal">
          <div className={`authorizers-modal-inner${isDetailView ? ' passes-modal-inner--detail' : ''}`}>
            <h3>{editing.id ? 'Detalle del pase' : 'Crear pase'}</h3>

            <PassesForm
              value={editing}
              onChange={handleEditingChange}
              companies={companies}
              error={fieldError || undefined}
              fieldErrors={fieldErrors}
              showPassDetail={isDetailView}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar pase?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={() => setIsAlertOpen(false)}
      />
    </MaintainerPageShell>
  );
};

export default PassesPage;
